import { Text } from "react-native";
import { styleConstantsV2 as C } from "~/style/styleConstantsV2";
import type { PropsWithChildren } from "react";
import type { TextProps, TextStyle } from "react-native";

interface BodyTextProps extends TextProps {
  style?: TextStyle;

  /**  ExtraLarge=18, Large=16, Medium=14, Small=12 */
  sizeExtraLarge?: boolean;
  /**  ExtraLarge=18, Large=16, Medium=14, Small=12 */
  sizeLarge?: boolean;
  /**  ExtraLarge=18, Large=16, Medium=14, Small=12 */
  sizeMedium?: boolean;
  /**  ExtraLarge=18, Large=16, Medium=14, Small=12 */
  sizeSmall?: boolean;

  colorPrimary?: boolean;
  colorBlack?: boolean;
  colorGrey?: boolean;
  colorGrey10?: boolean;
  colorGrey20?: boolean;
  colorGrey30?: boolean;
  colorGrey50?: boolean;
  colorGrey500?: boolean;

  colorWhite?: boolean;

  colorError?: boolean;

  // remove colors below when not needed
  colorLink?: boolean;
  /** Regular=400, Bold=700 */
  weightRegular?: boolean;
  /** Regular=400, Bold=700 */
  weightBold?: boolean;

  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  alignJustify?: boolean;

  underline?: boolean;
  lineThrough?: boolean;
  numberOfLines?: number;
}

/**
 * BodyText component for displaying text with specific styles.
 *
 * @param {Object} props - Component props.
 * @param {boolean} sizeExtraLarge - font size is 18px.
 * @param {boolean} sizeLarge - font size is 16px.
 * @param {boolean} sizeMedium - font size is 14px.
 * @param {boolean} sizeSmall - font size is 12px.
 * @param {boolean} weightRegular - font weight is 400.
 * @param {boolean} weightBold - font weight is 700.
 */
export const BodyText = ({
  children,
  style: passThroughStyle,
  sizeExtraLarge,
  sizeLarge,
  sizeMedium,
  sizeSmall,
  colorPrimary,
  colorBlack,
  colorGrey,
  colorGrey10,
  colorGrey20,
  colorGrey30,
  colorGrey50,
  colorLink,
  colorWhite,
  colorError,
  weightRegular,
  weightBold,
  alignCenter,
  alignLeft,
  alignRight,
  alignJustify,
  underline,
  lineThrough,
  numberOfLines,
  ...props
}: PropsWithChildren<BodyTextProps>) => {
  const style: TextStyle = {};

  style.fontFamily = "DMSans-Regular";
  style.fontSize = C.fontSizeBodyLarge;
  style.lineHeight = 26;

  if (weightRegular) {
    style.fontFamily = "DMSans-Regular";
  } else if (weightBold) {
    style.fontFamily = "DMSans-Bold";
  }

  if (sizeExtraLarge) {
    style.fontSize = C.fontSizeBodyExtraLarge;
    style.lineHeight = weightBold ? 24 : 27;
  } else if (sizeLarge) {
    style.fontSize = C.fontSizeBodyLarge;
    style.lineHeight = weightBold ? 24 : 26;
  } else if (sizeMedium) {
    style.fontSize = C.fontSizeBodyMedium;
    style.lineHeight = 19;
  } else if (sizeSmall) {
    style.fontSize = C.fontSizeBodySmall;
    style.lineHeight = 17;
  }

  if (colorPrimary) {
    style.color = C.colorPrimary;
  } else if (colorBlack) {
    style.color = C.colorBlack;
  } else if (colorGrey) {
    style.color = C.colorGrey;
  } else if (colorGrey10) {
    style.color = C.colorGrey10;
  } else if (colorGrey20) {
    style.color = C.colorGrey20;
  } else if (colorGrey30) {
    style.color = C.colorGrey30;
  } else if (colorGrey50) {
    style.color = C.colorGrey50;
  } else if (colorWhite) {
    style.color = C.colorWhite;
  } else if (colorLink) {
    style.color = C.colorLink;
  } else if (colorError) {
    style.color = C.colorError;
  }

  style.textAlign = "auto";
  if (alignCenter) style.textAlign = "center";
  else if (alignLeft) style.textAlign = "left";
  else if (alignRight) style.textAlign = "right";
  else if (alignJustify) style.textAlign = "justify";

  style.textDecorationLine = "none";
  if (underline) style.textDecorationLine = "underline";
  else if (lineThrough) style.textDecorationLine = "line-through";

  return (
    <Text
      numberOfLines={numberOfLines}
      style={[style, passThroughStyle]}
      {...props}
    >
      {children}
    </Text>
  );
};
